/* eslint-disable react/no-unescaped-entities, no-empty */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { getSettings, updateSettings, uploadCompanyLogo, deleteCompanyLogo, importBillitCustomers } from '../services/api';
import { Building2, Save, Hash, Globe, Upload, Image, CreditCard, MapPin, Phone, Mail, FileText, Trash2, Plug, CheckCircle2, XCircle, Loader2, CloudDownload, Check, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Send, ShoppingBag, Truck, Search } from 'lucide-react';
import { Ship } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { safeCopy } from '../utils/clipboard';

export default function Settings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('company');
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const t = searchParams.get('tab');
        if (t) setTab(t);
    }, [searchParams]);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoRef = useRef(null);
    const [settings, setSettings] = useState({
        name: '', slug: '',
        settings: { currency: 'EUR', timezone: 'Europe/Brussels' },
        order_settings: { prefix: 'ORD', start_number: 1, padding: 3 },
        company_info: { legal_name: '', address: '', city: '', postal_code: '', country: 'BE', phone: '', email: '', tax_number: '', tax_office: '', bank_name: '', iban: '', bic_swift: '', logo_url: '' },
    });

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            const r = await getSettings();
            setSettings(prev => ({
                ...prev, ...r.data,
                company_info: { ...prev.company_info, ...(r.data.company_info || {}) },
            }));
        } catch { toast.error('Ayarlar yuklenemedi'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateSettings(settings);
            toast.success('Ayarlar kaydedildi');
        } catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme hatasi'); }
        finally { setSaving(false); }
    };

    const set = (path, value) => {
        setSettings(prev => {
            const copy = JSON.parse(JSON.stringify(prev));
            const keys = path.split('.');
            let obj = copy;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!obj[keys[i]]) obj[keys[i]] = {};
                obj = obj[keys[i]];
            }
            obj[keys[keys.length - 1]] = value;
            return copy;
        });
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            const r = await uploadCompanyLogo(file);
            set('company_info.logo_url', r.data.logo_url);
            toast.success('Logo yuklendi');
        } catch { toast.error('Logo yuklenemedi'); }
        finally { setLogoUploading(false); }
    };

    const ci = settings.company_info || {};
    const orderPrefix = settings.order_settings?.prefix || 'ORD';
    const orderStart = settings.order_settings?.start_number || 1;
    const orderPad = settings.order_settings?.padding || 3;
    const currencies = ['EUR', 'USD', 'GBP', 'TRY'];
    const timezones = ['Europe/Brussels', 'Europe/Istanbul', 'Europe/London', 'UTC', 'America/New_York'];
    const API = process.env.REACT_APP_BACKEND_URL;

    const tabs = [
        { k: 'company', l: 'Sirket Bilgileri', icon: Building2 },
        { k: 'modules', l: 'Moduller', icon: ToggleLeft },
        { k: 'bank', l: 'Banka', icon: CreditCard },
        { k: 'orders', l: 'Siparisler', icon: Hash },
        { k: 'preferences', l: 'Tercihler', icon: Globe },
        { k: 'danger', l: 'Tehlikeli Bolge', icon: Trash2 },
    ];

    return (
        <DashboardLayout>
            <div className="space-y-5 max-w-3xl mx-auto" data-testid="settings-page">
                <div>
                    <h1 className="text-xl font-bold text-[#111827] mb-0.5">Ayarlar</h1>
                    <p className="text-sm text-[#6B7280]">Sirket ve uygulama ayarlarini yonetin</p>
                </div>

                <div className="flex gap-1 border-b border-[#E5E7EB] pb-px overflow-x-auto">
                    {tabs.map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? 'border-[#111827] text-[#111827]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
                            data-testid={`settings-tab-${t.k}`}>
                            <t.icon size={15} /> {t.l}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>
                ) : (
                    <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }} className="space-y-5" data-testid="settings-form">

                        {tab === 'company' && (
                            <div className="space-y-4">
                                {/* Logo */}
                                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                                    <h3 className="text-sm font-semibold text-[#111827] mb-3 flex items-center gap-2"><Image size={15} /> Logo</h3>
                                    <div className="flex items-center gap-4">
                                        <div className="w-20 h-20 rounded-xl border-2 border-dashed border-[#D1D5DB] flex items-center justify-center bg-[#F9FAFB] overflow-hidden flex-shrink-0">
                                            {ci.logo_url ? (
                                                <img src={`${API}${ci.logo_url}`} alt="Logo" className="w-full h-full object-contain" data-testid="company-logo-preview" />
                                            ) : (
                                                <Upload size={20} className="text-[#9CA3AF]" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <button type="button" onClick={() => logoRef.current?.click()} disabled={logoUploading}
                                                    className="inline-flex items-center gap-2 h-9 px-4 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] text-sm font-medium rounded-lg transition-colors disabled:opacity-50" data-testid="upload-logo-btn">
                                                    <Upload size={14} /> {logoUploading ? 'Yukleniyor...' : 'Logo Yukle'}
                                                </button>
                                                {ci.logo_url && (
                                                    <button type="button" onClick={async () => {
                                                        if (!window.confirm('Logoyu silmek istediginize emin misiniz?')) return;
                                                        try { await deleteCompanyLogo(); set('company_info.logo_url', ''); toast.success('Logo silindi'); }
                                                        catch { toast.error('Logo silinemedi'); }
                                                    }} className="inline-flex items-center gap-1 h-9 px-3 bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#EF4444] text-sm font-medium rounded-lg transition-colors" data-testid="delete-logo-btn">
                                                        <Trash2 size={14} /> Sil
                                                    </button>
                                                )}
                                            </div>
                                            <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                                            <p className="text-xs text-[#9CA3AF] mt-1">PNG, JPG, WebP (maks. 2MB)</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Company Details */}
                                <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                    <SettingRow label="Sirket Adi" desc="Faturalandirilacak sirket adi" icon={Building2}>
                                        <input type="text" value={ci.legal_name || settings.name || ''} onChange={e => { set('company_info.legal_name', e.target.value); if (!ci.legal_name) set('name', e.target.value); }}
                                            className="settings-input" placeholder="Sirket adi" data-testid="company-name-input" />
                                    </SettingRow>
                                    <SettingRow label="Slug (URL)" desc="Calisan giris URL'si" icon={Globe}>
                                        <input type="text" value={settings.slug || ''} disabled className="settings-input bg-[#F9FAFB] text-[#9CA3AF] cursor-not-allowed" data-testid="company-slug-display" />
                                    </SettingRow>
                                </div>

                                {/* Address */}
                                <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                    <div className="px-5 py-3 bg-[#F9FAFB] rounded-t-2xl">
                                        <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider flex items-center gap-1.5"><MapPin size={13} /> Adres</h3>
                                    </div>
                                    <SettingRow label="Adres" desc="Sokak, cadde, numara">
                                        <input type="text" value={ci.address || ''} onChange={e => set('company_info.address', e.target.value)} className="settings-input w-64" placeholder="Adres" data-testid="company-address-input" />
                                    </SettingRow>
                                    <SettingRow label="Sehir">
                                        <input type="text" value={ci.city || ''} onChange={e => set('company_info.city', e.target.value)} className="settings-input w-40" placeholder="Sehir" data-testid="company-city-input" />
                                    </SettingRow>
                                    <SettingRow label="Posta Kodu">
                                        <input type="text" value={ci.postal_code || ''} onChange={e => set('company_info.postal_code', e.target.value)} className="settings-input w-28" placeholder="1000" data-testid="company-postal-input" />
                                    </SettingRow>
                                    <SettingRow label="Ulke">
                                        <input type="text" value={ci.country || ''} onChange={e => set('company_info.country', e.target.value)} className="settings-input w-40" placeholder="Belcika" data-testid="company-country-input" />
                                    </SettingRow>
                                </div>

                                {/* Contact */}
                                <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                    <div className="px-5 py-3 bg-[#F9FAFB] rounded-t-2xl">
                                        <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider flex items-center gap-1.5"><Phone size={13} /> Iletisim</h3>
                                    </div>
                                    <SettingRow label="Telefon">
                                        <input type="text" value={ci.phone || ''} onChange={e => set('company_info.phone', e.target.value)} className="settings-input w-44" placeholder="+32 XXX XX XX" data-testid="company-phone-input" />
                                    </SettingRow>
                                    <SettingRow label="E-posta">
                                        <input type="email" value={ci.email || ''} onChange={e => set('company_info.email', e.target.value)} className="settings-input w-52" placeholder="info@sirket.com" data-testid="company-email-input" />
                                    </SettingRow>
                                    <SettingRow label="Vergi No" desc="BTW / KDV numarasi">
                                        <input type="text" value={ci.tax_number || ''} onChange={e => set('company_info.tax_number', e.target.value)} className="settings-input w-44" placeholder="BE0XXX.XXX.XXX" data-testid="company-tax-input" />
                                    </SettingRow>
                                    <SettingRow label="Vergi Dairesi">
                                        <input type="text" value={ci.tax_office || ''} onChange={e => set('company_info.tax_office', e.target.value)} className="settings-input w-44" placeholder="Vergi dairesi" data-testid="company-tax-office-input" />
                                    </SettingRow>
                                </div>
                            </div>
                        )}

                        {tab === 'bank' && (
                            <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                <div className="px-5 py-3 bg-[#F9FAFB] rounded-t-2xl">
                                    <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider flex items-center gap-1.5"><CreditCard size={13} /> Banka Hesap Bilgileri</h3>
                                    <p className="text-xs text-[#9CA3AF] mt-0.5">PDF fatura ve siparislerde goruntulenir</p>
                                </div>
                                <SettingRow label="Banka Adi">
                                    <input type="text" value={ci.bank_name || ''} onChange={e => set('company_info.bank_name', e.target.value)} className="settings-input w-52" placeholder="ING, KBC..." data-testid="bank-name-input" />
                                </SettingRow>
                                <SettingRow label="IBAN">
                                    <input type="text" value={ci.iban || ''} onChange={e => set('company_info.iban', e.target.value)} className="settings-input w-64 font-mono text-xs" placeholder="BE00 0000 0000 0000" data-testid="bank-iban-input" />
                                </SettingRow>
                                <SettingRow label="BIC / SWIFT">
                                    <input type="text" value={ci.bic_swift || ''} onChange={e => set('company_info.bic_swift', e.target.value)} className="settings-input w-36 font-mono text-xs" placeholder="BBRUBEBB" data-testid="bank-bic-input" />
                                </SettingRow>
                            </div>
                        )}

                        {tab === 'orders' && (
                            <div className="space-y-4">
                                <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                    <SettingRow label="Prefix" desc="Siparis numarasinin on eki (orn: ORD, SIP)">
                                        <input type="text" value={orderPrefix} maxLength={10}
                                            onChange={e => set('order_settings.prefix', e.target.value.toUpperCase())}
                                            className="settings-input w-28" placeholder="ORD" data-testid="order-prefix-input" />
                                    </SettingRow>
                                    <SettingRow label="Baslangic No" desc="Ilk siparis numarasi">
                                        <input type="number" value={orderStart} min={1}
                                            onChange={e => set('order_settings.start_number', parseInt(e.target.value) || 1)}
                                            className="settings-input w-28" data-testid="order-start-input" />
                                    </SettingRow>
                                    <SettingRow label="Hane Sayisi" desc="Numara uzunlugu (orn: 3 -> 001)">
                                        <select value={orderPad} onChange={e => set('order_settings.padding', parseInt(e.target.value))}
                                            className="settings-input w-36" data-testid="order-padding-select">
                                            <option value={2}>2 hane (01, 02...)</option>
                                            <option value={3}>3 hane (001, 002...)</option>
                                            <option value={4}>4 hane (0001, 0002...)</option>
                                            <option value={5}>5 hane (00001...)</option>
                                        </select>
                                    </SettingRow>
                                </div>
                                <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB]">
                                    <span className="text-xs text-[#6B7280]">Onizleme:</span>
                                    <span className="font-mono font-bold text-sm text-[#111827]" data-testid="order-preview">
                                        {orderPrefix}-{String(orderStart).padStart(orderPad, '0')}
                                    </span>
                                </div>
                            </div>
                        )}

                        {tab === 'preferences' && (
                            <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                                <SettingRow label="Para Birimi" desc="Varsayilan para birimi">
                                    <select value={settings.settings?.currency || 'EUR'} onChange={e => set('settings.currency', e.target.value)}
                                        className="settings-input w-28" data-testid="currency-select">
                                        {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </SettingRow>
                                <SettingRow label="Saat Dilimi" desc="Islemlerde kullanilacak saat dilimi">
                                    <select value={settings.settings?.timezone || 'Europe/Brussels'} onChange={e => set('settings.timezone', e.target.value)}
                                        className="settings-input w-52" data-testid="timezone-select">
                                        {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                    </select>
                                </SettingRow>
                            </div>
                        )}

                        {tab === 'api' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
                                <b>API ayarlari tasindi.</b> Tum entegrasyonlar artik <a href="/integrations" className="underline font-semibold">Entegrasyonlar</a> sayfasinda.
                            </div>
                        )}

                        {tab === 'modules' && <ModulesTab />}

                        {tab === 'danger' && <DangerZoneTab />}

                        {tab !== 'modules' && tab !== 'danger' && tab !== 'api' && (
                        <div className="flex justify-end">
                            <button type="submit" disabled={saving}
                                className="inline-flex items-center gap-2 h-10 px-6 bg-[#111827] hover:bg-[#1F2937] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                                data-testid="save-settings-btn">
                                <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                        )}
                    </form>
                )}
            </div>
        </DashboardLayout>
    );
}

function SettingRow({ label, desc, icon: Icon, children }) {
    return (
        <div className="flex items-center justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
                <div className="text-sm font-medium text-[#111827] flex items-center gap-1.5">
                    {Icon && <Icon size={13} className="text-[#9CA3AF]" />}
                    {label}
                </div>
                {desc && <div className="text-xs text-[#9CA3AF] mt-0.5">{desc}</div>}
            </div>
            <div className="flex-shrink-0">{children}</div>
        </div>
    );
}

// Iter 98.2: Tum API entegrasyonlari tek sayfada accordion seklinde
function IntegrationsPage() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const [open, setOpen] = useState('');
    const [statuses, setStatuses] = useState({});
    const [search, setSearch] = useState('');

    // Connection status'larını paralel olarak çek
    useEffect(() => {
        const token = localStorage.getItem('token');
        const h = { headers: { Authorization: `Bearer ${token}` } };
        const checks = [
            { k: 'billit', url: '/api/billit/config', test: (d) => !!d.api_key && d.enabled },
            { k: 'bol', url: '/api/bol/config', test: (d) => !!(d.client_id && d.client_secret) },
            { k: 'telegram', url: '/api/telegram/config', test: (d) => !!d.has_token && !!d.chat_id && d.enabled !== false },
            { k: 'transport', url: '/api/transport/config', test: (d) => !!(d.quicargo_configured || d.cargoboard_configured) },
            { k: 'shipsgo', url: '/api/logistics/config', test: (d) => !!(d.shipsgo_configured || d.api_key) },
        ];
        Promise.allSettled(
            checks.map(c => axios.get(`${API}${c.url}`, h).then(r => ({ k: c.k, ok: c.test(r.data || {}) })).catch(() => ({ k: c.k, ok: false })))
        ).then(results => {
            const map = {};
            results.forEach(r => { if (r.status === 'fulfilled') map[r.value.k] = r.value.ok; });
            setStatuses(map);
        });
    }, [API]);

    const sections = useMemo(() => ([
        // Pazaryeri
        { k: 'bol', cat: 'Pazaryeri', l: 'Bol.com', desc: 'Siparis cekme · komisyon hesabi · reklam maliyeti', icon: ShoppingBag, color: 'blue', comp: <BolApiTab /> },
        // Muhasebe
        { k: 'billit', cat: 'Muhasebe', l: 'Billit', desc: 'Fatura yonetimi · musteri/urun senkronizasyonu', icon: FileText, color: 'purple', comp: <BillitApiTab /> },
        // Lojistik
        { k: 'shipsgo', cat: 'Lojistik', l: 'ShipsGo', desc: 'Konteyner / BL takibi · gercek zamanli liman olaylari', icon: Ship, color: 'cyan', comp: <ShipsGoApiTab /> },
        // Nakliye
        { k: 'transport', cat: 'Nakliye', l: 'Quicargo & CargoBoard', desc: 'Karayolu nakliye · pallet/FTL fiyat sorgu ve siparis', icon: Truck, color: 'amber', comp: <TransportApiTab /> },
        // Bildirim
        { k: 'telegram', cat: 'Bildirim', l: 'Telegram', desc: 'Yeni siparis + gunluk/haftalik rapor bildirimleri', icon: Send, color: 'sky', comp: <TelegramTab /> },
    ]), []);

    const filtered = sections.filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return s.l.toLowerCase().includes(q) || s.cat.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q);
    });

    // Group by category
    const byCat = {};
    filtered.forEach(s => { (byCat[s.cat] = byCat[s.cat] || []).push(s); });
    const catOrder = ['Pazaryeri', 'Muhasebe', 'Lojistik', 'Nakliye', 'Bildirim'];

    const connectedCount = Object.values(statuses).filter(Boolean).length;
    const totalCount = sections.length;

    return (
        <div className="space-y-4" data-testid="integrations-page">
            {/* Header card with summary */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white rounded-xl p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <h2 className="text-base font-bold flex items-center gap-2"><Plug size={16} /> Entegrasyonlar</h2>
                    <p className="text-xs text-gray-300 mt-0.5">Dis sistemlere baglantilari buradan yonet</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-gray-300">Aktif</div>
                        <div className="text-2xl font-black">
                            <span className="text-emerald-400">{connectedCount}</span>
                            <span className="text-gray-400 text-sm font-bold"> / {totalCount}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Entegrasyon ara (Bol, ShipsGo, fatura, nakliye...)"
                    className="w-full h-9 pl-9 pr-3 text-sm border border-gray-300 rounded-lg focus:border-blue-500 outline-none bg-white"
                    data-testid="integrations-search" />
            </div>

            {/* Grouped sections */}
            {catOrder.filter(c => byCat[c]).map(cat => (
                <div key={cat}>
                    <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-2 px-1">{cat}</div>
                    <div className="space-y-2">
                        {byCat[cat].map(s => {
                            const isOpen = open === s.k;
                            const Icon = s.icon;
                            const isConnected = statuses[s.k];
                            const colorMap = {
                                blue: 'bg-blue-50 text-blue-700', purple: 'bg-purple-50 text-purple-700',
                                cyan: 'bg-cyan-50 text-cyan-700', amber: 'bg-amber-50 text-amber-700',
                                sky: 'bg-sky-50 text-sky-700',
                            };
                            return (
                                <div key={s.k} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                                    <button onClick={() => setOpen(isOpen ? '' : s.k)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
                                        data-testid={`integration-toggle-${s.k}`}>
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[s.color]}`}>
                                            <Icon size={16} />
                                        </div>
                                        <div className="flex-1 text-left min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm font-bold text-gray-900">{s.l}</div>
                                                {isConnected ? (
                                                    <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded">Bagli</span>
                                                ) : (
                                                    <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-gray-100 text-gray-500 rounded">Bagli degil</span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-gray-500 truncate">{s.desc}</div>
                                        </div>
                                        {isOpen ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronRight size={16} className="text-gray-500 shrink-0" />}
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                                            {s.comp}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {filtered.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500 bg-white border border-gray-200 rounded-xl">
                    Aradiginiz entegrasyon bulunamadi.
                </div>
            )}
        </div>
    );
}


function BillitApiTab() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const [apiKey, setApiKey] = useState('');
    const [partyId, setPartyId] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [status, setStatus] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showKey, setShowKey] = useState(false);
    const [accountInfo, setAccountInfo] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        axios.get(`${API}/api/billit/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => {
                if (r.data.api_key) { setApiKey(r.data.api_key); setPartyId(r.data.party_id || ''); setEnabled(r.data.enabled || false); setStatus(r.data.status || null); setAccountInfo(r.data.account_info || null); }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [API]);

    const toggleEnabled = async (newVal) => {
        setEnabled(newVal);
        if (!newVal && apiKey) {
            try {
                const token = localStorage.getItem('token');
                await axios.put(`${API}/api/billit/config`, { api_key: apiKey, party_id: partyId, enabled: false, account_info: accountInfo }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Billit entegrasyonu devre disi birakildi');
            } catch {}
        }
    };

    const testConnection = async () => {
        if (!apiKey.trim()) { toast.error('API Key giriniz'); return; }
        setTesting(true); setStatus(null); setAccountInfo(null);
        try {
            const token = localStorage.getItem('token');
            const r = await axios.post(`${API}/api/billit/test`, { api_key: apiKey, party_id: partyId || null }, { headers: { Authorization: `Bearer ${token}` } });
            setStatus('connected');
            setStatusMsg(r.data.message || 'Baglanti basarili');
            setAccountInfo(r.data.account_info || null);
            if (r.data.party_id && !partyId) setPartyId(r.data.party_id);
            toast.success('Billit baglantisi basarili!');
        } catch (err) {
            setStatus('error');
            setStatusMsg(err.response?.data?.detail || 'Baglanti hatasi');
            toast.error('Billit baglantisi basarisiz');
        } finally { setTesting(false); }
    };

    const saveConfig = async () => {
        if (!apiKey.trim()) { toast.error('API Key giriniz'); return; }
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/billit/config`, { api_key: apiKey, party_id: partyId, enabled: true, account_info: accountInfo }, { headers: { Authorization: `Bearer ${token}` } });
            setEnabled(true);
            toast.success('Billit API ayarlari kaydedildi');
        } catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme hatasi'); }
        finally { setSaving(false); }
    };

    const disconnect = async () => {
        if (!window.confirm('Billit baglantisini kaldirmak istediginize emin misiniz?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/api/billit/config`, { headers: { Authorization: `Bearer ${token}` } });
            setApiKey(''); setPartyId(''); setEnabled(false); setStatus(null); setAccountInfo(null); setStatusMsg('');
            toast.success('Billit baglantisi kaldirildi');
        } catch { toast.error('Hata olustu'); }
    };

    const handleImportCustomers = async () => {
        if (importing) return;
        setImporting(true);
        setImportResult(null);
        try {
            const res = await importBillitCustomers();
            setImportResult(res.data);
            if (res.data.success) {
                toast.success(res.data.message || 'Musteriler aktarildi');
            }
        } catch (err) {
            const errMsg = err.response?.data?.detail || 'Import hatasi';
            setImportResult({ success: false, error: errMsg });
            toast.error(errMsg);
        } finally {
            setImporting(false);
        }
    };

    if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>;

    return (
        <div className="space-y-4" data-testid="billit-api-tab">
            {/* Toggle Card */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB]">
                <div
                    className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer select-none"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleEnabled(!enabled); }}
                    data-testid="billit-toggle-row"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${enabled ? 'bg-green-100' : 'bg-[#F3F4F6]'}`}>
                            <Plug size={16} className={enabled ? 'text-green-600' : 'text-[#9CA3AF]'} />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-[#111827]">Billit Entegrasyonu</div>
                            <div className="text-xs text-[#9CA3AF] mt-0.5">{enabled ? 'Aktif — irsaliyeler Billit\'e gonderilir' : 'Pasif — detaylari gormek icin aktif edin'}</div>
                        </div>
                    </div>
                    <div
                        role="switch"
                        aria-checked={enabled}
                        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                        data-testid="billit-enabled-toggle"
                    >
                        <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </div>
                </div>
            </div>

            {/* Detaylar: sadece enabled iken gorunur */}
            {enabled && (
                <>
                    {/* Status Banner */}
                    {status && (
                        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${status === 'connected' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="billit-status-banner">
                            {status === 'connected' ? <CheckCircle2 size={18} className="text-green-600 shrink-0" /> : <XCircle size={18} className="text-red-500 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium ${status === 'connected' ? 'text-green-800' : 'text-red-800'}`}>
                                    {status === 'connected' ? 'Billit Baglandi' : 'Baglanti Hatasi'}
                                </div>
                                <div className={`text-xs mt-0.5 ${status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>{statusMsg}</div>
                            </div>
                            {status === 'connected' && (
                                <button type="button" onClick={disconnect} className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-testid="billit-disconnect-btn">
                                    Baglantiyi Kaldir
                                </button>
                            )}
                        </div>
                    )}

                    {/* Account Info */}
                    {accountInfo && (
                        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="billit-account-info">
                            <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-3">Billit Hesap Bilgileri</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {accountInfo.company_name && (
                                    <div><div className="text-[10px] text-[#9CA3AF]">Sirket</div><div className="text-sm font-medium text-[#111]">{accountInfo.company_name}</div></div>
                                )}
                                {accountInfo.vat_number && (
                                    <div><div className="text-[10px] text-[#9CA3AF]">Vergi No</div><div className="text-sm font-mono text-[#111]">{accountInfo.vat_number}</div></div>
                                )}
                                {accountInfo.biller_id && (
                                    <div><div className="text-[10px] text-[#9CA3AF]">Party ID</div><div className="text-sm font-mono text-[#111]">{accountInfo.biller_id}</div></div>
                                )}
                                {accountInfo.email && (
                                    <div><div className="text-[10px] text-[#9CA3AF]">E-posta</div><div className="text-sm text-[#111]">{accountInfo.email}</div></div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* API Key & Party ID */}
                    <div className="bg-white rounded-2xl border border-[#E5E7EB] divide-y divide-[#F3F4F6]">
                        <div className="px-5 py-3 bg-[#F9FAFB] rounded-t-2xl">
                            <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider flex items-center gap-1.5">
                                <Plug size={13} /> API Ayarlari
                            </h3>
                        </div>

                        <div className="flex items-center justify-between gap-4 px-5 py-4">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-[#111827]">API Key</div>
                                <div className="text-xs text-[#9CA3AF] mt-0.5">Billit API anahtari</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type={showKey ? 'text' : 'password'}
                                    value={apiKey}
                                    onChange={e => setApiKey(e.target.value)}
                                    placeholder="API anahtarinizi yapistiriniz"
                                    className="settings-input w-64 font-mono text-xs"
                                    data-testid="billit-api-key-input"
                                />
                                <button type="button" onClick={() => setShowKey(!showKey)}
                                    className="text-xs text-[#6B7280] hover:text-[#111] px-2 py-1.5 rounded-lg hover:bg-[#F3F4F6] transition-colors">
                                    {showKey ? 'Gizle' : 'Goster'}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-4 px-5 py-4">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-[#111827]">Party ID</div>
                                <div className="text-xs text-[#9CA3AF] mt-0.5">Test basarili olunca otomatik cekilir</div>
                            </div>
                            <input
                                type="text"
                                value={partyId}
                                onChange={e => setPartyId(e.target.value)}
                                placeholder="Otomatik cekilecek..."
                                className="settings-input w-48 font-mono text-xs"
                                data-testid="billit-party-id-input"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <button type="button" onClick={testConnection} disabled={testing || !apiKey.trim()}
                            className="inline-flex items-center gap-2 h-10 px-5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold rounded-xl transition-all disabled:opacity-50"
                            data-testid="billit-test-btn">
                            {testing ? <Loader2 size={15} className="animate-spin" /> : <Plug size={15} />}
                            {testing ? 'Test Ediliyor...' : 'Baglantiyi Test Et'}
                        </button>
                        <button type="button" onClick={saveConfig} disabled={saving || !apiKey.trim()}
                            className="inline-flex items-center gap-2 h-10 px-5 bg-[#111827] hover:bg-[#1F2937] text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50"
                            data-testid="billit-save-btn">
                            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                            {saving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>

                    {/* Customer Import */}
                    <div className="bg-white rounded-2xl border border-[#E5E7EB]">
                        <div className="px-5 py-3 bg-[#F9FAFB] rounded-t-2xl">
                            <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider flex items-center gap-1.5">
                                <CloudDownload size={13} /> Musteri Import
                            </h3>
                            <p className="text-xs text-[#9CA3AF] mt-0.5">Billit'teki musterileri sisteme aktarin</p>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <button type="button" onClick={handleImportCustomers} disabled={importing || !apiKey.trim()}
                                className="inline-flex items-center gap-2 h-10 px-5 bg-orange-50 hover:bg-orange-100 text-orange-700 text-sm font-semibold rounded-xl transition-all disabled:opacity-50"
                                data-testid="billit-import-customers-btn">
                                {importing ? <Loader2 size={15} className="animate-spin" /> : <CloudDownload size={15} />}
                                {importing ? 'Icerik Aktariliyor...' : 'Musterileri Billit\'ten Aktar'}
                            </button>
                            {importResult && (
                                <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${importResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="billit-import-result">
                                    {importResult.success ? <Check size={16} className="text-green-600 mt-0.5 shrink-0" /> : <XCircle size={16} className="text-red-500 mt-0.5 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                            {importResult.success ? importResult.message : importResult.error}
                                        </div>
                                        {importResult.stats && (
                                            <div className="mt-1.5 flex items-center gap-3 text-xs text-[#6B7280]">
                                                <span>Toplam: <strong>{importResult.stats.total}</strong></span>
                                                <span>Yeni: <strong className="text-green-600">{importResult.stats.created}</strong></span>
                                                <span>Guncellenen: <strong className="text-blue-600">{importResult.stats.updated}</strong></span>
                                                <span>Atlanan: <strong>{importResult.stats.skipped}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Help Text */}
                    <div className="bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] p-4">
                        <h4 className="text-xs font-semibold text-[#374151] mb-2">API Key Nasil Alinir?</h4>
                        <ol className="text-xs text-[#6B7280] space-y-1.5 list-decimal list-inside">
                            <li>Billit hesabiniza giris yapin (app.billit.be)</li>
                            <li><strong>Settings &gt; API</strong> bolumune gidin</li>
                            <li>Yeni bir API token olusturun</li>
                            <li>Token'i kopyalayip yukaridaki alana yapistiriniz</li>
                        </ol>
                    </div>
                </>
            )}
        </div>
    );
}


function DocumentTypesTab() {
    const [types, setTypes] = useState([]);
    const [docs, setDocs] = useState([]);
    const [newName, setNewName] = useState('');
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [companySlug, setCompanySlug] = useState('');
    const [filterType, setFilterType] = useState('all');
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const terminalUrl = companySlug ? `${window.location.origin}/terminal/documents/${companySlug}` : '';

    // Fetch company slug from /api/auth/me
    useEffect(() => {
        if (token) {
            axios.get(`${API}/api/auth/me`, { headers })
                .then(r => {
                    if (r.data.company?.slug) {
                        setCompanySlug(r.data.company.slug);
                    }
                })
                .catch(() => {});
        }
    }, [token]);

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/api/document-types`, { headers }),
            axios.get(`${API}/api/documents`, { headers }),
        ]).then(([tRes, dRes]) => {
            setTypes(tRes.data);
            setDocs(dRes.data);
        }).catch(() => toast.error('Veriler yuklenemedi'))
        .finally(() => setLoading(false));
    }, []);

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        setAdding(true);
        try {
            const r = await axios.post(`${API}/api/document-types`, { name }, { headers });
            setTypes(prev => [...prev, r.data]);
            setNewName('');
            toast.success('Dokuman turu eklendi');
        } catch (e) { toast.error(e.response?.data?.detail || 'Eklenemedi'); }
        finally { setAdding(false); }
    };

    const handleDeleteType = async (id) => {
        try {
            await axios.delete(`${API}/api/document-types/${id}`, { headers });
            setTypes(prev => prev.filter(t => t.id !== id));
            toast.success('Silindi');
        } catch { toast.error('Silinemedi'); }
    };

    const handleDeleteDoc = async (id) => {
        try {
            await axios.delete(`${API}/api/documents/${id}`, { headers });
            setDocs(prev => prev.filter(d => d.id !== id));
            toast.success('Dokuman silindi');
        } catch { toast.error('Silinemedi'); }
    };

    const filteredDocs = filterType === 'all' ? docs : docs.filter(d => d.document_type_id === filterType);

    return (
        <div className="space-y-4" data-testid="document-types-tab">
            {/* Terminal Link */}
            {terminalUrl && (
                <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
                    <p className="text-xs font-semibold text-[#6D28D9] mb-1.5">Dokuman Terminal Linki</p>
                    <div className="flex items-center gap-2">
                        <input type="text" readOnly value={terminalUrl}
                            className="flex-1 h-9 px-3 bg-white border border-[#DDD6FE] rounded-lg text-xs text-[#111] font-mono"
                            data-testid="doc-terminal-link" />
                        <button onClick={() => { safeCopy(terminalUrl, 'Kopyalandi'); }}
                            className="h-9 px-3 bg-[#8B5CF6] text-white text-xs font-medium rounded-lg hover:bg-[#7C3AED] transition-colors"
                            data-testid="copy-doc-terminal-link">
                            Kopyala
                        </button>
                    </div>
                    <p className="text-[10px] text-[#8B5CF6] mt-1.5">Calisanlar bu linki kullanarak PIN ile dokuman yukleyebilir</p>
                </div>
            )}

            {/* Add New Type */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <h3 className="text-sm font-semibold text-[#111] mb-3">Yeni Dokuman Turu Ekle</h3>
                <div className="flex gap-2">
                    <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
                        placeholder="ornek: Fatura, Irsaliye, Sertifika..."
                        className="flex-1 h-10 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm placeholder:text-[#C5C5C7] outline-none focus:border-[#8B5CF6]"
                        data-testid="new-doc-type-input" />
                    <button onClick={handleAdd} disabled={adding || !newName.trim()}
                        className="h-10 px-5 bg-[#8B5CF6] text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:bg-[#7C3AED] transition-colors"
                        data-testid="add-doc-type-btn">
                        {adding ? 'Ekleniyor...' : 'Ekle'}
                    </button>
                </div>
            </div>

            {/* Types List */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="px-4 py-2.5 border-b border-[#F3F4F6]">
                    <h3 className="text-sm font-semibold text-[#111]">Dokuman Turleri ({types.length})</h3>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>
                ) : types.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Henuz dokuman turu eklenmemis</div>
                ) : (
                    <div className="divide-y divide-[#F3F4F6]">
                        {types.map(t => (
                            <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-[#F9FAFB] transition-colors" data-testid={`doc-type-row-${t.id}`}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                                        <FileText size={13} className="text-[#8B5CF6]" />
                                    </div>
                                    <span className="text-sm text-[#111] font-medium">{t.name}</span>
                                </div>
                                <button onClick={() => handleDeleteType(t.id)}
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 transition-colors"
                                    data-testid={`delete-doc-type-${t.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Uploaded Documents List */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid="uploaded-documents-list">
                <div className="px-4 py-2.5 border-b border-[#F3F4F6] flex items-center justify-between flex-wrap gap-2">
                    <h3 className="text-sm font-semibold text-[#111]">Yuklenen Dokumanlar ({filteredDocs.length})</h3>
                    {types.length > 0 && (
                        <select value={filterType} onChange={e => setFilterType(e.target.value)}
                            className="h-8 px-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-xs text-[#111] outline-none"
                            data-testid="doc-filter-type">
                            <option value="all">Tum Turler</option>
                            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    )}
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>
                ) : filteredDocs.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#9CA3AF]">Henuz dokuman yuklenmemis</div>
                ) : (
                    <div className="divide-y divide-[#F3F4F6]">
                        {filteredDocs.map(d => (
                            <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9FAFB] transition-colors" data-testid={`doc-row-${d.id}`}>
                                {d.file_url && d.file_url.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                                    <img src={`${API}${d.file_url}`} alt="" className="w-10 h-10 rounded-lg object-cover border border-[#E5E7EB] shrink-0" />
                                ) : (
                                    <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center shrink-0">
                                        <FileText size={16} className="text-[#8B5CF6]" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs font-medium px-2 py-0.5 bg-[#F3F4F6] rounded-md text-[#6B7280]">{d.document_type_name}</span>
                                        {d.document_number && <span className="text-xs text-[#111] font-medium">#{d.document_number}</span>}
                                    </div>
                                    <div className="text-[11px] text-[#9CA3AF] mt-0.5">
                                        {d.employee_name} · {d.created_at ? new Date(d.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                        {d.notes && <span className="ml-1 text-[#6B7280]">— {d.notes}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {d.file_url && (
                                        <a href={`${API}${d.file_url}`} target="_blank" rel="noopener noreferrer"
                                            className="h-7 px-2.5 rounded-lg text-[10px] font-medium text-[#8B5CF6] hover:bg-[#8B5CF6]/10 flex items-center transition-colors"
                                            data-testid={`view-doc-${d.id}`}>
                                            Gor
                                        </a>
                                    )}
                                    <button onClick={() => handleDeleteDoc(d.id)}
                                        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#D1D5DB] hover:text-red-500 hover:bg-red-50 transition-colors"
                                        data-testid={`delete-doc-${d.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function ModulesTab() {
    const [moduleData, setModuleData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        axios.get(`${API}/api/modules`, { headers })
            .then(r => setModuleData(r.data))
            .catch(() => toast.error('Moduller yuklenemedi'))
            .finally(() => setLoading(false));
    }, []);

    const handleToggle = (key) => {
        if (!moduleData) return;
        const def = moduleData.definitions.find(d => d.key === key);
        if (def?.core) return; // core modules can't be disabled
        setModuleData(prev => ({
            ...prev,
            modules: { ...prev.modules, [key]: !prev.modules[key] }
        }));
    };

    const handleSave = async () => {
        if (!moduleData) return;
        setSaving(true);
        try {
            const r = await axios.put(`${API}/api/modules`, { modules: moduleData.modules }, { headers });
            setModuleData(prev => ({ ...prev, modules: r.data.modules }));
            // Update auth context
            const { setModules } = await import('../context/AuthContext');
            toast.success('Moduller kaydedildi. Sayfa yenileniyor...');
            setTimeout(() => window.location.reload(), 1000);
        } catch { toast.error('Kaydedilemedi'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="p-8 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div>;
    if (!moduleData) return null;

    return (
        <div className="space-y-4" data-testid="modules-tab">
            <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-4">
                <p className="text-sm text-[#0369A1]">Kullanmak istediginiz modulleri acip kapatabilirsiniz. Kapatilan moduller menuden gizlenir.</p>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden divide-y divide-[#F3F4F6]">
                {moduleData.definitions.map(def => {
                    const enabled = moduleData.modules[def.key] !== false;
                    return (
                        <div key={def.key} className="flex items-center justify-between px-4 py-3.5 hover:bg-[#F9FAFB] transition-colors" data-testid={`module-row-${def.key}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#111]">{def.name}</span>
                                    {def.core && <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E5E7EB] text-[#6B7280]">Temel</span>}
                                </div>
                                <p className="text-xs text-[#9CA3AF] mt-0.5">{def.description}</p>
                            </div>
                            <button
                                onClick={() => handleToggle(def.key)}
                                disabled={def.core}
                                className={`shrink-0 ml-3 transition-colors ${def.core ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                data-testid={`module-toggle-${def.key}`}
                            >
                                {enabled ? (
                                    <ToggleRight size={28} className="text-[#059669]" />
                                ) : (
                                    <ToggleLeft size={28} className="text-[#D1D5DB]" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-end">
                <button onClick={handleSave} disabled={saving}
                    className="inline-flex items-center gap-2 h-10 px-6 bg-[#111827] hover:bg-[#1F2937] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                    data-testid="save-modules-btn">
                    <Save size={15} /> {saving ? 'Kaydediliyor...' : 'Modulleri Kaydet'}
                </button>
            </div>
        </div>
    );
}


function DangerZoneTab() {
    const [busy, setBusy] = useState(false);
    const [typed, setTyped] = useState('');
    const CONFIRM_PHRASE = 'TUM MUHASEBE VERILERINI SIL';
    const API = process.env.REACT_APP_BACKEND_URL;
    const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    const doReset = async () => {
        if (typed !== CONFIRM_PHRASE) {
            toast.error('Onay metnini tam olarak yazin');
            return;
        }
        if (!window.confirm('SON UYARI: Tum muhasebe verileri (firmalar, faturalar, banka hareketleri, mutabakat onerileri) KALICI olarak silinecek. Emin misin?')) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/api/accounting/reset-all-accounting-data`,
                { confirm: 'RESET_ALL_ACCOUNTING_DATA' },
                { headers: headers() }
            );
            const d = r.data?.deleted || {};
            const lines = Object.entries(d).map(([k, v]) => `${k}: ${v}`).join('\n');
            toast.success(`Toplam ${r.data?.total || 0} kayit silindi`, { duration: 8000, description: lines });
            setTyped('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Silme hatasi');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="settings-danger-zone">
            <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                    <Trash2 size={18} className="text-red-600" />
                    <h2 className="text-base font-bold text-red-700">Tum Muhasebe Verilerini Sifirla</h2>
                </div>
                <p className="text-sm text-red-800 mb-3 leading-relaxed">
                    Bu islem <strong>geri alinamaz</strong>. Asagidaki tum veriler kalici olarak silinir:
                </p>
                <ul className="text-xs text-red-700 list-disc ml-5 mb-4 space-y-0.5">
                    <li>Tum firma kayitlari (musteri + tedarikci)</li>
                    <li>Tum satis & alis faturalari (iadeler dahil)</li>
                    <li>Tum banka hareketleri ve mutabakat eslesmeleri</li>
                    <li>Mutabakat onerileri, AI alias kayitlari, birlestirme gecmisi</li>
                </ul>
                <p className="text-xs text-red-800 mb-2">
                    Devam etmek icin asagidaki kutuya tam olarak yazin: <code className="bg-white px-2 py-0.5 rounded text-red-700 font-bold">{CONFIRM_PHRASE}</code>
                </p>
                <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="Onay metnini buraya yazin..."
                    className="w-full h-10 px-3 border-2 border-red-300 rounded-lg text-sm font-mono focus:outline-none focus:border-red-500 mb-3"
                    data-testid="settings-danger-confirm-input"
                />
                <button
                    onClick={doReset}
                    disabled={busy || typed !== CONFIRM_PHRASE}
                    className="inline-flex items-center gap-2 h-10 px-6 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="settings-danger-reset-btn"
                >
                    <Trash2 size={15} /> {busy ? 'Siliniyor...' : 'Tum Muhasebe Verilerini Sil'}
                </button>
            </div>
            <p className="text-xs text-[#6B7280] px-2">
                Not: Stok, siparis ve diger modul verileri etkilenmez. Yalnizca muhasebe (accounting) collection'lari sifirlanir.
            </p>
        </div>
    );
}


// ─── Bol.com API Config Tab (Iter 97) ──────────────────────────────────────
function BolApiTab() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [savedClientId, setSavedClientId] = useState('');
    const [advClientId, setAdvClientId] = useState('');
    const [advClientSecret, setAdvClientSecret] = useState('');
    const [savedAdvClientId, setSavedAdvClientId] = useState('');
    const [lastSync, setLastSync] = useState('');
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const token = localStorage.getItem('token');

    useEffect(() => {
        axios.get(`${API}/api/bol/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setSavedClientId(r.data?.client_id || '');
                setClientId(r.data?.client_id || '');
                setSavedAdvClientId(r.data?.adv_client_id || '');
                setAdvClientId(r.data?.adv_client_id || '');
                setLastSync(r.data?.last_sync_at || '');
                setStats(r.data?.last_sync_stats || null);
            })
            .catch(() => { /* ignore */ });
    }, [token, API]);

    const save = async () => {
        // Partial update destekli: en az bir credential set'i gerekli.
        const hasRetailer = clientId.trim() !== '';
        const hasAdv = advClientId.trim() !== '';
        if (!hasRetailer && !hasAdv) {
            toast.error('Retailer veya Advertising client_id\'den en az birini gir');
            return;
        }
        // Retailer girilmişse: ID değişiyorsa veya ilk kayıtsa secret de zorunlu
        if (hasRetailer && clientId !== savedClientId && !clientSecret) {
            toast.error('Retailer Client ID yeni: Client Secret de gir');
            return;
        }
        // Advertising girilmişse: ID değişiyorsa veya ilk kayıtsa secret de zorunlu
        if (hasAdv && advClientId !== savedAdvClientId && !advClientSecret) {
            toast.error('Advertising Client ID yeni: Client Secret de gir');
            return;
        }
        setBusy(true);
        try {
            const payload = {};
            if (hasRetailer) {
                payload.client_id = clientId;
                payload.client_secret = clientSecret;  // boş = backend mevcut'u korur
            }
            if (hasAdv) {
                payload.adv_client_id = advClientId;
                payload.adv_client_secret = advClientSecret;
            }
            await axios.put(`${API}/api/bol/config`, payload,
                { headers: { Authorization: `Bearer ${token}` } });
            if (hasRetailer) setSavedClientId(clientId);
            setClientSecret('');
            if (hasAdv) setSavedAdvClientId(advClientId);
            setAdvClientSecret('');
            toast.success('Bol.com ayarlari kaydedildi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        } finally { setBusy(false); }
    };

    const testAdv = async () => {
        if (!savedAdvClientId) { toast.error('Once Advertising client kaydet'); return; }
        setTestBusy(true);
        try {
            const r = await axios.post(`${API}/api/bol/test-adv`, {}, { headers: { Authorization: `Bearer ${token}` } });
            const d = r.data || {};
            if (d.ok) {
                toast.success(`✓ Advertising baglantisi calisiyor — son 30 gun maliyet: ${d.cost?.toFixed?.(2) || 0} €`, { duration: 7000 });
            } else {
                toast.error(d.detail || 'Advertising baglantisi basarisiz');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test basarisiz');
        } finally { setTestBusy(false); }
    };

    const test = async () => {
        setTestBusy(true);
        try {
            await axios.post(`${API}/api/bol/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Bol.com baglantisi basarili');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test basarisiz');
        } finally { setTestBusy(false); }
    };

    return (
        <div className="space-y-3" data-testid="bol-api-tab">
            {/* RETAILER */}
            <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Retailer API (zorunlu)</div>
                <p className="text-[11px] text-gray-500 mb-3">
                    Bol partnerplatform → Settings → API → "Client credentials for the Retailer API" → Create
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Client ID</label>
                        <input value={clientId} onChange={(e) => setClientId(e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                            data-testid="bol-client-id" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                            Client Secret {savedClientId && <span className="text-emerald-600 font-normal">✓</span>}
                        </label>
                        <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)}
                            placeholder={savedClientId ? '•••• (degistirmek icin gir)' : 'Secret'}
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                            data-testid="bol-client-secret" />
                    </div>
                </div>
            </div>

            {/* ADVERTISING */}
            <div className="pt-3 border-t">
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Advertising API (opsiyonel)</div>
                <p className="text-[11px] text-gray-500 mb-3">
                    Reklam maliyeti icin: Bol panel → API → "Client credentials for the Advertising API" → Create
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Advertising Client ID</label>
                        <input value={advClientId} onChange={(e) => setAdvClientId(e.target.value)}
                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                            data-testid="bol-adv-client-id" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                            Advertising Client Secret {savedAdvClientId && <span className="text-emerald-600 font-normal">✓</span>}
                        </label>
                        <input type="password" value={advClientSecret} onChange={(e) => setAdvClientSecret(e.target.value)}
                            placeholder={savedAdvClientId ? '•••• (degistirmek icin gir)' : 'Secret'}
                            className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                            data-testid="bol-adv-client-secret" />
                    </div>
                </div>
            </div>

            {/* AKSIYONLAR */}
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                <button onClick={save} disabled={busy}
                    className="h-9 px-4 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 rounded-md"
                    data-testid="bol-save-btn">
                    {busy ? 'Kaydediliyor...' : 'Tumunu Kaydet'}
                </button>
                {savedClientId && (
                    <button onClick={test} disabled={testBusy}
                        className="h-9 px-3 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 rounded-md"
                        data-testid="bol-test-btn">
                        Retailer Test
                    </button>
                )}
                {savedAdvClientId && (
                    <button onClick={testAdv} disabled={testBusy}
                        className="h-9 px-3 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 rounded-md"
                        data-testid="bol-test-adv-btn">
                        Advertising Test
                    </button>
                )}
                {lastSync && (
                    <div className="ml-auto text-[11px] text-gray-500">
                        Son sync: {lastSync.slice(0, 16).replace('T', ' ')}
                        {stats && ` · ${stats.fetched || 0} siparis`}
                    </div>
                )}
            </div>

            {/* WEBHOOK / PUSH NOTIFICATIONS */}
            {savedClientId && <BolWebhookSection />}
        </div>
    );
}

function BolWebhookSection() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = () => {
        setLoading(true);
        axios.get(`${API}/api/bol/subscriptions`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setSubs(r.data?.subscriptions || []))
            .catch(() => setSubs([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const subscribe = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/api/bol/subscribe`, {},
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Webhook aktif: ${r.data?.events?.join(', ')}`, { duration: 5000 });
            load();
        } catch (e) {
            const detail = e.response?.data?.detail || 'Hata';
            toast.error(detail.slice(0, 200));
        } finally { setBusy(false); }
    };

    const unsubscribe = async (subId) => {
        if (!window.confirm('Bu webhook aboneligi kaldirilsin mi?')) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/api/bol/subscriptions/${subId}`,
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Abonelik kaldirildi');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setBusy(false); }
    };

    return (
        <div className="pt-3 border-t" data-testid="bol-webhook-section">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-600">Push Webhook (anlik bildirim)</div>
                    <p className="text-[11px] text-gray-500 mt-1">
                        Yeni siparis, kargo ve durum degisiklikleri 10dk cron beklemeden anlik gelir.
                    </p>
                </div>
                {subs.length === 0 ? (
                    <button onClick={subscribe} disabled={busy}
                        className="h-9 px-4 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-md whitespace-nowrap"
                        data-testid="bol-subscribe-btn">
                        {busy ? 'Baglaniliyor...' : '🔔 Webhook\'a Abone Ol'}
                    </button>
                ) : null}
            </div>

            {loading ? (
                <div className="text-xs text-gray-400 py-2">Yukleniyor...</div>
            ) : subs.length === 0 ? (
                <div className="text-[11px] text-gray-500 italic py-1">Henuz aktif abonelik yok.</div>
            ) : (
                <div className="space-y-1.5">
                    {subs.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs"
                            data-testid={`bol-sub-${s.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-emerald-900">✓ Aktif · {(s.resources || []).join(', ')}</div>
                                <div className="text-[10px] text-emerald-700 truncate font-mono">{s.url}</div>
                            </div>
                            <button onClick={() => unsubscribe(s.id)} disabled={busy}
                                className="h-7 px-2.5 text-[11px] font-semibold text-rose-700 bg-white hover:bg-rose-50 border border-rose-200 rounded-md whitespace-nowrap disabled:opacity-50"
                                data-testid={`bol-unsub-${s.id}`}>
                                Kaldir
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}



function TelegramTab() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const [botToken, setBotToken] = useState('');
    const [chatId, setChatId] = useState('');
    const [hasToken, setHasToken] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [newOrder, setNewOrder] = useState(true);
    const [daily, setDaily] = useState(true);
    const [weekly, setWeekly] = useState(true);
    const [busy, setBusy] = useState(false);
    const [chats, setChats] = useState([]);
    const [finding, setFinding] = useState(false);

    useEffect(() => {
        axios.get(`${API}/api/telegram/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setHasToken(r.data?.has_token || false);
                setChatId(r.data?.chat_id || '');
                setEnabled(r.data?.enabled !== false);
                setNewOrder(r.data?.new_order !== false);
                setDaily(r.data?.daily_report !== false);
                setWeekly(r.data?.weekly_report !== false);
            }).catch(() => { /* ignore */ });
    }, [token, API]);

    const findChatId = async () => {
        if (!botToken && !hasToken) { toast.error('Once bot token gir'); return; }
        setFinding(true);
        try {
            // Use entered token or load from server if not entered
            const tokenToUse = botToken;
            if (!tokenToUse) { toast.error('Chat ID bulmak icin token de girmelisin (sadece bu test icin)'); setFinding(false); return; }
            const r = await axios.get(`${API}/api/telegram/find-chat-id`,
                { params: { token: tokenToUse }, headers: { Authorization: `Bearer ${token}` } });
            const found = r.data?.chats || [];
            setChats(found);
            if (!found.length) {
                toast.warning('Hicbir mesaj bulunamadi. Once Telegram\'da bot a /start veya merhaba yaz, sonra tekrar dene.', { duration: 8000 });
            } else if (found.length === 1) {
                setChatId(found[0].chat_id);
                toast.success(`Chat ID otomatik dolduruldu: ${found[0].name}`);
            } else {
                toast.info(`${found.length} chat bulundu — birini sec`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Chat ID bulunamadi');
        } finally { setFinding(false); }
    };

    const save = async () => {
        if (!hasToken && !botToken) { toast.error('Bot token gerekli'); return; }
        if (!chatId) { toast.error('Chat ID gerekli'); return; }
        setBusy(true);
        try {
            await axios.put(`${API}/api/telegram/config`, {
                bot_token: botToken,  // bos ise backend mevcudu korur
                chat_id: chatId,
                enabled, new_order: newOrder, daily_report: daily, weekly_report: weekly,
            }, { headers: { Authorization: `Bearer ${token}` } });
            if (botToken) setHasToken(true);
            setBotToken('');
            toast.success('Telegram ayarlari kaydedildi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kaydedilemedi');
        } finally { setBusy(false); }
    };

    const test = async () => {
        if (!hasToken) { toast.error('Once kaydet'); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/api/telegram/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Test mesaji Telegram\'a gonderildi 📲');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test basarisiz');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-4">
                <div>
                    <h3 className="text-base font-bold text-gray-900">Telegram Bildirim Botu</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Yeni Bol siparis + gunluk (22:00) + haftalik (Pazar 20:00) rapor Telegram'a otomatik gonderilir.
                        Bot olusturma: <span className="font-mono text-gray-700">@BotFather</span> &rsaquo; /newbot komutu.
                    </p>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Bot Token {hasToken && <span className="text-emerald-700">✓ kayitli</span>}
                    </label>
                    <input type="password" value={botToken} onChange={(e) => setBotToken(e.target.value)}
                        placeholder={hasToken ? '•••••••• (degistirmek icin yeniden gir)' : '123456789:AAEhBOweik...'}
                        className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                        data-testid="tg-bot-token" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Chat ID</label>
                    <div className="flex gap-2">
                        <input value={chatId} onChange={(e) => setChatId(e.target.value)}
                            placeholder="8763590467"
                            className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                            data-testid="tg-chat-id" />
                        <button onClick={findChatId} disabled={finding}
                            className="h-9 px-3 text-xs font-semibold rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 whitespace-nowrap"
                            data-testid="tg-find-chat-btn">
                            {finding ? 'Aranıyor...' : 'Chat ID Bul'}
                        </button>
                    </div>
                    {chats.length > 1 && (
                        <div className="mt-2 space-y-1">
                            {chats.map((c) => (
                                <button key={c.chat_id} onClick={() => setChatId(c.chat_id)}
                                    className={`block w-full text-left px-2 py-1 text-xs rounded ${chatId === c.chat_id ? 'bg-sky-100 text-sky-900 font-semibold' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                    {c.name} <span className="text-gray-500">({c.chat_id} · {c.type})</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                        Chat ID otomatik bulmak icin once Telegram'da bot'a herhangi bir mesaj yaz, sonra "Chat ID Bul"a bas.
                    </div>
                </div>
                <div className="border-t pt-4 space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="tg-enabled" />
                        <span>Telegram bildirimlerini aktif et</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer ml-4">
                        <input type="checkbox" checked={newOrder} onChange={(e) => setNewOrder(e.target.checked)} disabled={!enabled} data-testid="tg-new-order" />
                        <span>🛒 Yeni siparis (anlik)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer ml-4">
                        <input type="checkbox" checked={daily} onChange={(e) => setDaily(e.target.checked)} disabled={!enabled} data-testid="tg-daily" />
                        <span>📊 Gunluk rapor (her gun 22:00)</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer ml-4">
                        <input type="checkbox" checked={weekly} onChange={(e) => setWeekly(e.target.checked)} disabled={!enabled} data-testid="tg-weekly" />
                        <span>📅 Haftalik rapor (Pazar 20:00)</span>
                    </label>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <button onClick={test} disabled={busy || !hasToken}
                        className="h-9 px-4 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid="tg-test-btn">
                        Test Mesaj Gonder
                    </button>
                    <button onClick={save} disabled={busy}
                        className="h-9 px-4 text-sm font-semibold rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                        data-testid="tg-save-btn">
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
}

function TransportApiTab() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const [cfg, setCfg] = useState(null);
    const [qInput, setQInput] = useState('');
    const [cbInput, setCbInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [pickup, setPickup] = useState({});

    const load = () => {
        axios.get(`${API}/api/transport/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { setCfg(r.data); setPickup(r.data?.default_pickup || {}); })
            .catch(() => setCfg({}));
    };
    useEffect(load, []); // eslint-disable-line

    const saveKey = async (provider, key) => {
        if (!key) { toast.error('Anahtar bos'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/transport/config`, { [`${provider}_api_key`]: key },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Anahtar kaydedildi');
            if (provider === 'quicargo') setQInput('');
            if (provider === 'cargoboard') setCbInput('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setSaving(false); }
    };

    const removeKey = async (provider) => {
        if (!window.confirm(`${provider} anahtari silinsin mi?`)) return;
        try {
            await axios.delete(`${API}/api/transport/config/${provider}`,
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Silindi');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const savePickup = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/api/transport/config`, { default_pickup: pickup },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Default pickup kaydedildi');
        } catch { toast.error('Hata'); }
        finally { setSaving(false); }
    };

    if (!cfg) return <div className="text-sm text-gray-500 py-4">Yukleniyor...</div>;

    return (
        <div className="space-y-4" data-testid="transport-settings">
            {/* Quicargo */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-sm font-bold text-gray-900">Quicargo API</div>
                        <div className="text-[11px] text-gray-500">Hollanda merkezli, pallet/LTL/FTL nakliye fiyatlari</div>
                    </div>
                    {cfg.quicargo_configured ? (
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                                ✓ {cfg.quicargo_key_mask}
                            </span>
                            <button onClick={() => removeKey('quicargo')}
                                className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50 rounded"
                                data-testid="rm-quicargo">Kaldir</button>
                        </div>
                    ) : <span className="text-[11px] text-gray-400 italic">Henuz baglanmadi</span>}
                </div>
                <div className="flex items-center gap-2">
                    <input type="password" value={qInput} onChange={(e) => setQInput(e.target.value)}
                        placeholder="Quicargo API anahtarini yapistir"
                        className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                        data-testid="quicargo-key-input" />
                    <button onClick={() => saveKey('quicargo', qInput)} disabled={saving || !qInput}
                        className="h-9 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
                        data-testid="save-quicargo-key">Kaydet</button>
                </div>
            </div>

            {/* CargoBoard */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-sm font-bold text-gray-900">CargoBoard API</div>
                        <div className="text-[11px] text-gray-500">Avrupa B2B nakliye (henuz aktif degil — cevap bekleniyor)</div>
                    </div>
                    {cfg.cargoboard_configured ? (
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                                ✓ {cfg.cargoboard_key_mask}
                            </span>
                            <button onClick={() => removeKey('cargoboard')}
                                className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50 rounded"
                                data-testid="rm-cargoboard">Kaldir</button>
                        </div>
                    ) : <span className="text-[11px] text-gray-400 italic">Henuz baglanmadi</span>}
                </div>
                <div className="flex items-center gap-2">
                    <input type="password" value={cbInput} onChange={(e) => setCbInput(e.target.value)}
                        placeholder="CargoBoard API anahtari (henuz hazir degil)"
                        className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                        data-testid="cargoboard-key-input" />
                    <button onClick={() => saveKey('cargoboard', cbInput)} disabled={saving || !cbInput}
                        className="h-9 px-4 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
                        data-testid="save-cargoboard-key">Kaydet</button>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="flex items-start gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!cfg.cargoboard_sandbox}
                            onChange={async (e) => {
                                try {
                                    await axios.post(`${API}/api/transport/config`,
                                        { cargoboard_sandbox: e.target.checked },
                                        { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success(e.target.checked ? 'Sandbox modu aktif' : 'Production modu aktif');
                                    load();
                                } catch { toast.error('Hata'); }
                            }}
                            data-testid="cargoboard-sandbox-toggle" className="mt-0.5" />
                        <div>
                            <div className="font-semibold text-gray-800">
                                Sandbox Modu {cfg.cargoboard_sandbox && <span className="text-amber-600">(aktif)</span>}
                            </div>
                            <div className="text-[11px] text-gray-500">
                                <b>Production key</b> kullaniyorsan KAPALI birak (default). <b>Test/sandbox key</b> kullaniyorsan ac.
                                <br/>403 Forbidden hatasi aliyorsan bu modu degistirmen lazim.
                            </div>
                        </div>
                    </label>
                </div>
            </div>

            {/* Default Pickup */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-bold text-gray-900 mb-2">Default Yukleme Adresi</div>
                <p className="text-[11px] text-gray-500 mb-3">Bu adres yeni fiyat sorgularinda Pickup formuna otomatik dolar.</p>
                <div className="grid grid-cols-2 gap-2">
                    {[
                        ['Sirket', 'company'], ['Iletisim', 'contact_name'],
                        ['Cadde', 'street_name'], ['No', 'street_number'],
                        ['Posta Kodu', 'zipcode'], ['Sehir', 'city'],
                        ['Ulke (NL/BE...)', 'country'], ['Telefon', 'phone'],
                    ].map(([label, k]) => (
                        <label key={k} className="block">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
                            <input value={pickup[k] || ''} onChange={(e) => setPickup({ ...pickup, [k]: e.target.value })}
                                className="w-full h-8 px-2 text-xs border border-gray-300 rounded mt-0.5"
                                data-testid={`pickup-${k}`} />
                        </label>
                    ))}
                </div>
                <div className="flex justify-end mt-3">
                    <button onClick={savePickup} disabled={saving}
                        className="h-8 px-3 text-xs font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-md"
                        data-testid="save-pickup">Default Adresi Kaydet</button>
                </div>
            </div>
        </div>
    );
}


function ShipsGoApiTab() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const [cfg, setCfg] = useState(null);
    const [input, setInput] = useState('');
    const [secretInput, setSecretInput] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => {
        axios.get(`${API}/api/logistics/config`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setCfg(r.data))
            .catch(() => setCfg({}));
    };
    useEffect(load, []); // eslint-disable-line

    const saveToken = async () => {
        if (!input) { toast.error('Anahtar bos'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/api/logistics/config`, { shipsgo_token: input },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('ShipsGo anahtari kaydedildi');
            setInput('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setSaving(false); }
    };

    const saveSecret = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/api/logistics/config`, { webhook_secret: secretInput },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success(secretInput ? 'Webhook secret kaydedildi' : 'Webhook secret temizlendi');
            setSecretInput('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setSaving(false); }
    };

    const removeToken = async () => {
        if (!window.confirm('ShipsGo anahtari silinsin mi?')) return;
        try {
            await axios.delete(`${API}/api/logistics/config`,
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Silindi');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : API}/api/logistics/webhook/shipsgo`;

    if (!cfg) return <div className="text-sm text-gray-500 py-4">Yukleniyor...</div>;

    return (
        <div className="space-y-4" data-testid="shipsgo-settings">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-sm font-bold text-gray-900">ShipsGo API Token</div>
                        <div className="text-[11px] text-gray-500">Deniz konteyneri / BL / Booking takibi · 160+ taşıyıcı</div>
                    </div>
                    {cfg.shipsgo_configured ? (
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                                ✓ {cfg.shipsgo_key_mask}
                            </span>
                            <button onClick={removeToken}
                                className="h-7 px-2 text-[11px] text-rose-600 hover:bg-rose-50 rounded"
                                data-testid="rm-shipsgo">Kaldir</button>
                        </div>
                    ) : <span className="text-[11px] text-gray-400 italic">Henuz baglanmadi</span>}
                </div>
                <div className="flex items-center gap-2">
                    <input type="password" value={input} onChange={(e) => setInput(e.target.value)}
                        placeholder="ShipsGo API token (UUID format)"
                        className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                        data-testid="shipsgo-token-input" />
                    <button onClick={saveToken} disabled={saving || !input}
                        className="h-9 px-4 text-sm font-semibold text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 rounded-md"
                        data-testid="save-shipsgo-token">Kaydet</button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">
                    Token'i <a href="https://shipsgo.com/dashboard/air/integrations/api-tokens" target="_blank" rel="noreferrer" className="text-cyan-700 underline">ShipsGo Dashboard</a>'tan al.
                </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-sm font-bold text-gray-900 mb-2">Webhook URL</div>
                <p className="text-[11px] text-gray-500 mb-2">
                    ShipsGo Dashboard → <i>Webhooks</i> bölümünde aşağıdaki URL'yi ekle ve <b>OCEAN.SHIPMENTS.SHIPMENT_UPDATED</b> event'ine abone ol.
                    Konteyner durumu değiştikçe Telegram'a anlık bildirim gelir.
                </p>
                <div className="flex items-center gap-2 mb-3">
                    <code className="flex-1 px-3 py-2 text-[11px] bg-gray-50 border border-gray-200 rounded font-mono text-gray-700 break-all">{webhookUrl}</code>
                    <button onClick={() => { safeCopy(webhookUrl, 'Kopyalandi'); }}
                        className="h-8 px-3 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
                        data-testid="copy-webhook-url">Kopyala</button>
                </div>
                <div className="text-xs font-semibold text-gray-600 mb-1">HMAC Secret (opsiyonel)</div>
                <p className="text-[11px] text-gray-500 mb-2">
                    ShipsGo'da webhook için "Secret Key" tanımladıysan buraya da gir; gelen istekleri doğrularız.
                </p>
                <div className="flex items-center gap-2">
                    <input type="password" value={secretInput} onChange={(e) => setSecretInput(e.target.value)}
                        placeholder={cfg.webhook_secret_set ? '*** kayitli — değiştirmek için yaz' : 'Webhook secret (opsiyonel)'}
                        className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-md font-mono"
                        data-testid="shipsgo-secret-input" />
                    <button onClick={saveSecret} disabled={saving}
                        className="h-9 px-4 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 rounded-md"
                        data-testid="save-shipsgo-secret">Kaydet</button>
                </div>
            </div>
        </div>
    );
}

